import { GraduationCap, HeartHandshake, Compass, ArrowRight } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { AmfButton, Overline, Section, Divider } from "../components/Primitives";
import { Link } from "react-router-dom";
import { IMAGES, MISSION_BAND, IMPACT } from "../data/content";
import { useContent } from "../context/ContentContext";

const ICONS = { GraduationCap, HeartHandshake, Compass };

export default function Home() {
  const { t } = useContent();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="A Ghanaian schoolgirl in uniform looking forward with quiet confidence"
          className="absolute inset-0 w-full h-full object-cover amf-img"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-amf-navy via-amf-navy/70 to-transparent" />
        <div className="relative h-full max-w-[1200px] mx-auto px-6 md:px-10 flex items-center">
          <FadeIn className="max-w-2xl">
            <Overline light className="block mb-5">
              {t("home.hero.eyebrow")}
            </Overline>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-amf-cream">
              {t("home.hero.headline")}
            </h1>
            <p className="font-lora text-lg text-amf-cream/85 mt-6 leading-relaxed max-w-xl">
              {t("home.hero.subheadline")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <AmfButton to="/about" data-testid="hero-learn-button" variant="ghost">
                {t("home.hero.cta1")}
              </AmfButton>
              <AmfButton to="/donate" data-testid="hero-donate-button">
                {t("home.hero.cta2")}
              </AmfButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MISSION BAND */}
      <Section className="bg-amf-cream">
        <FadeIn className="text-center max-w-2xl mx-auto mb-14">
          <Overline>{t("home.mission.eyebrow")}</Overline>
          <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3">
            {t("home.mission.heading")}
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-amf-gold/30 border border-amf-gold/30">
          {MISSION_BAND.map((m, i) => {
            const Icon = ICONS[m.icon];
            return (
              <FadeIn
                key={m.title}
                delay={i * 0.12}
                className="bg-amf-cream p-9 md:p-10"
              >
                <Icon size={34} strokeWidth={1.4} className="text-amf-burgundy" />
                <h3 className="font-playfair text-2xl text-amf-navy mt-5">
                  {m.title}
                </h3>
                <p className="font-lora text-amf-charcoal/80 mt-3 leading-relaxed">
                  {m.text}
                </p>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* IMPACT */}
      <Section className="bg-amf-navy">
        <FadeIn className="text-center mb-14">
          <Overline light>{t("home.impact.eyebrow")}</Overline>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {IMPACT.map((s, i) => (
            <FadeIn key={i} delay={i * 0.12} className="text-center px-4">
              <p className="font-playfair text-5xl md:text-6xl text-amf-gold">
                {s.number}
              </p>
              <p className="font-lora text-amf-cream/80 mt-4 leading-relaxed max-w-xs mx-auto">
                {s.label}
              </p>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* FEATURED STORY */}
      <Section className="bg-amf-cream">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <FadeIn className="lg:col-span-6">
            <img
              src={IMAGES.classroomGroup}
              alt="Girls at Dzorwulu Basic School during the Menstrual Dignity Support Initiative"
              className="w-full aspect-[4/3] object-cover amf-img"
            />
          </FadeIn>
          <FadeIn delay={0.12} className="lg:col-span-6">
            <Overline>{t("home.featured.eyebrow")}</Overline>
            <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3 leading-tight">
              {t("home.featured.heading")}
            </h2>
            <p className="font-lora text-amf-charcoal/80 mt-5 leading-relaxed">
              {t("home.featured.text")}
            </p>
            <Link
              to="/publications/menstrual-dignity-dzorwulu"
              data-testid="featured-story-link"
              className="amf-overline inline-flex items-center gap-2 text-amf-burgundy mt-7 hover:gap-3 transition-all"
            >
              Read the Full Story <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </Section>

      {/* CTA BAND */}
      <section className="relative">
        <img
          src={IMAGES.girlsStudy}
          alt="Young women studying together"
          className="absolute inset-0 w-full h-full object-cover amf-img"
        />
        <div className="absolute inset-0 bg-amf-navy/85" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <FadeIn>
            <h2 className="font-playfair text-3xl md:text-5xl text-amf-cream leading-tight max-w-3xl mx-auto">
              {t("home.cta.heading")}
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <AmfButton to="/contact#partner" variant="ghost">Partner With Us</AmfButton>
              <AmfButton to="/contact#volunteer" variant="ghost">Volunteer</AmfButton>
              <AmfButton to="/donate">Donate</AmfButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
